// src/components/MotivationalQuotes.js
import React, { useState, useEffect } from 'react';
import api from '../api';
import { toast } from 'react-hot-toast';
import {
  IoAdd,
  IoTrashOutline,
  IoCreateOutline,
  IoCheckmarkCircle,
  IoCloseCircle,
  IoSparklesOutline,
  IoSaveOutline,
  IoCloseOutline,
} from 'react-icons/io5';

const MotivationalQuotes = () => {
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState('');
  const [editAuthor, setEditAuthor] = useState('');
  const [newText, setNewText] = useState('');
  const [newAuthor, setNewAuthor] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);

  const fetchQuotes = async () => {
    try {
      const res = await api.get('/admin/quotes');
      setQuotes(res.data);
    } catch {
      toast.error('Error al cargar frases');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchQuotes(); }, []);

  const handleToggle = async (q) => {
    try {
      const res = await api.put(`/admin/quotes/${q.id}`, { is_active: q.is_active === 1 ? 0 : 1 });
      setQuotes(prev => prev.map(x => x.id === q.id ? res.data : x));
    } catch {
      toast.error('Error al cambiar estado');
    }
  };

  const startEdit = (q) => {
    setEditingId(q.id);
    setEditText(q.text);
    setEditAuthor(q.author || '');
  };

  const cancelEdit = () => { setEditingId(null); setEditText(''); setEditAuthor(''); };

  const saveEdit = async () => {
    if (!editText.trim()) return;
    setSaving(true);
    try {
      const res = await api.put(`/admin/quotes/${editingId}`, { text: editText.trim(), author: editAuthor.trim() || null });
      setQuotes(prev => prev.map(x => x.id === editingId ? res.data : x));
      cancelEdit();
      toast.success('Frase actualizada');
    } catch {
      toast.error('Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar esta frase?')) return;
    try {
      await api.delete(`/admin/quotes/${id}`);
      setQuotes(prev => prev.filter(x => x.id !== id));
      toast.success('Frase eliminada');
    } catch {
      toast.error('Error al eliminar');
    }
  };

  const handleCreate = async () => {
    if (!newText.trim()) return;
    setSaving(true);
    try {
      const res = await api.post('/admin/quotes', { text: newText.trim(), author: newAuthor.trim() || null, is_active: 1 });
      setQuotes(prev => [res.data, ...prev]);
      setNewText('');
      setNewAuthor('');
      setShowForm(false);
      toast.success('Frase creada');
    } catch {
      toast.error('Error al crear frase');
    } finally {
      setSaving(false);
    }
  };

  const activeCount = quotes.filter(q => q.is_active === 1).length;

  if (loading) return (
    <div className="w-full container mx-auto p-4 md:p-8 text-white">
      <div className="space-y-3 animate-pulse">
        {[1,2,3].map(i => <div key={i} className="h-20 rounded-xl bg-white/5" />)}
      </div>
    </div>
  );

  return (
    <div className="w-full container mx-auto p-4 md:p-8 text-white animate-fade-up">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <div>
          <div className="flex items-center gap-3 mb-1">
            <IoSparklesOutline size={28} className="text-marizan-red" />
            <h1 className="text-3xl md:text-4xl font-bold">Frases Motivacionales</h1>
          </div>
          <p className="text-gray-400 text-sm">
            {quotes.length} frases · <span className="text-green-400 font-semibold">{activeCount} activas</span>
          </p>
        </div>
        <button
          onClick={() => setShowForm(!showForm)}
          className="flex items-center gap-2 bg-marizan-red hover:bg-red-700 text-white px-5 py-2.5 rounded-xl font-semibold transition-all hover:scale-105 active:scale-95"
        >
          <IoAdd size={20} /> Nueva frase
        </button>
      </div>

      {/* New quote form */}
      {showForm && (
        <div className="glass-panel border border-marizan-red/30 rounded-2xl p-5 mb-6 animate-fade-up">
          <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <IoAdd size={18} className="text-marizan-red" /> Nueva frase
          </h2>
          <textarea
            rows={3}
            value={newText}
            onChange={e => setNewText(e.target.value)}
            placeholder="Escribe la frase motivacional..."
            className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white text-sm focus:ring-2 focus:ring-marizan-red outline-none resize-none mb-3"
          />
          <input
            type="text"
            value={newAuthor}
            onChange={e => setNewAuthor(e.target.value)}
            placeholder="Autor (opcional)"
            className="w-full px-4 py-2.5 bg-white/5 border border-white/10 rounded-xl text-white text-sm focus:ring-2 focus:ring-marizan-red outline-none mb-4"
          />
          <div className="flex gap-3 justify-end">
            <button onClick={() => setShowForm(false)} className="flex items-center gap-1.5 px-4 py-2 text-gray-400 hover:text-white transition-colors text-sm">
              <IoCloseOutline size={18} /> Cancelar
            </button>
            <button
              onClick={handleCreate}
              disabled={saving || !newText.trim()}
              className="flex items-center gap-2 bg-marizan-red hover:bg-red-700 text-white px-5 py-2 rounded-xl text-sm font-semibold transition-all disabled:opacity-50"
            >
              <IoSaveOutline size={16} /> {saving ? 'Guardando...' : 'Guardar'}
            </button>
          </div>
        </div>
      )}

      {/* Quotes list */}
      <div className="space-y-3">
        {quotes.length === 0 && (
          <div className="text-center border-2 border-dashed border-gray-700 rounded-2xl p-12">
            <IoSparklesOutline size={40} className="mx-auto text-gray-600 mb-3" />
            <p className="text-gray-400">No hay frases todavía. ¡Crea la primera!</p>
          </div>
        )}
        {quotes.map(q => (
          <div
            key={q.id}
            className={`glass-panel rounded-xl p-4 border transition-all ${q.is_active === 1 ? 'border-white/10' : 'border-white/5 opacity-50'}`}
          >
            {editingId === q.id ? (
              /* Edit mode */
              <div className="space-y-3">
                <textarea
                  rows={3}
                  value={editText}
                  onChange={e => setEditText(e.target.value)}
                  className="w-full px-3 py-2.5 bg-white/5 border border-white/20 rounded-lg text-white text-sm focus:ring-2 focus:ring-marizan-red outline-none resize-none"
                />
                <input
                  type="text"
                  value={editAuthor}
                  onChange={e => setEditAuthor(e.target.value)}
                  placeholder="Autor (opcional)"
                  className="w-full px-3 py-2 bg-white/5 border border-white/20 rounded-lg text-white text-sm focus:ring-2 focus:ring-marizan-red outline-none"
                />
                <div className="flex gap-2 justify-end">
                  <button onClick={cancelEdit} className="flex items-center gap-1 px-3 py-1.5 text-gray-400 hover:text-white text-sm transition-colors">
                    <IoCloseOutline size={16} /> Cancelar
                  </button>
                  <button
                    onClick={saveEdit}
                    disabled={saving}
                    className="flex items-center gap-1.5 bg-marizan-red hover:bg-red-700 text-white px-4 py-1.5 rounded-lg text-sm font-semibold transition-all disabled:opacity-50"
                  >
                    <IoSaveOutline size={15} /> Guardar
                  </button>
                </div>
              </div>
            ) : (
              /* View mode */
              <div className="flex items-start gap-3">
                <div className="flex-1 min-w-0">
                  <p className="text-white/90 text-sm leading-relaxed italic">"{q.text}"</p>
                  {q.author && <p className="text-marizan-red text-xs mt-1 font-semibold">— {q.author}</p>}
                </div>
                <div className="flex items-center gap-2 flex-shrink-0 mt-0.5">
                  {/* Toggle on/off */}
                  <button
                    onClick={() => handleToggle(q)}
                    title={q.is_active === 1 ? 'Desactivar' : 'Activar'}
                    className="transition-colors"
                  >
                    {q.is_active === 1
                      ? <IoCheckmarkCircle size={22} className="text-green-400 hover:text-green-300" />
                      : <IoCloseCircle size={22} className="text-gray-600 hover:text-gray-400" />
                    }
                  </button>
                  {/* Edit */}
                  <button onClick={() => startEdit(q)} title="Editar" className="text-gray-500 hover:text-white transition-colors p-1">
                    <IoCreateOutline size={18} />
                  </button>
                  {/* Delete */}
                  <button onClick={() => handleDelete(q.id)} title="Eliminar" className="text-gray-600 hover:text-red-400 transition-colors p-1">
                    <IoTrashOutline size={18} />
                  </button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default MotivationalQuotes;
